import React from 'react';
import { Box, Typography, Button, Stack, Alert } from '@mui/material';
import { Refresh as RefreshIcon, ErrorOutline } from '@mui/icons-material';

/**
 * Catches a render error inside its children and renders a friendly fallback
 * with a retry button instead of white-screening the whole tab.
 *
 * Use around scene roots (Synthetic, Network, Alerts) so a single crash in a
 * nested chart/grid doesn't take the entire module out.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.label || 'unknown', error, info);
    this.setState({ info });
  }

  componentDidUpdate(prevProps) {
    // Auto-recover when the caller changes `resetKey` (e.g. navigating to a
    // different tab/view) — otherwise a crash on one view stays pinned and
    // looks like the whole section is broken / "reloading".
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, info: null });
    }
  }

  handleRetry = () => {
    this.setState({ error: null, info: null });
    if (typeof this.props.onRetry === 'function') this.props.onRetry();
  };

  render() {
    if (!this.state.error) return this.props.children;

    // Tile-sized fallback for small surfaces (a dashboard widget), where the
    // full-page treatment below would overflow the tile. Isolating a crash to
    // one widget is the point: without a boundary at this level, a single bad
    // widget render takes the whole dashboard down.
    if (this.props.compact) {
      return (
        <Box sx={{
          height: '100%', width: '100%', p: 1.5,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 0.5, textAlign: 'center',
        }}>
          <ErrorOutline color="error" fontSize="small" />
          <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>
            {this.props.title || 'This widget failed to render'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden', wordBreak: 'break-word',
          }}>
            {String(this.state.error?.message || this.state.error)}
          </Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={this.handleRetry} sx={{ mt: 0.5 }}>
            Retry
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <Stack spacing={2} sx={{ maxWidth: 720, width: '100%' }}>
          <Alert severity="error" icon={<ErrorOutline />}>
            <Typography variant="subtitle1" fontWeight={600}>
              {this.props.title || 'Something went wrong'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {this.props.description || 'A component crashed while rendering. The rest of the app is still usable.'}
            </Typography>
            <Box component="pre" sx={{
              mt: 1, fontSize: 12, overflow: 'auto', maxHeight: 160,
              bgcolor: 'rgba(0,0,0,0.04)', p: 1, borderRadius: 1,
            }}>
              {String(this.state.error?.message || this.state.error)}
            </Box>
          </Alert>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={this.handleRetry}>
              Retry
            </Button>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Reload page
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }
}
